import React from 'react';

/**
 * Revenue Cycle Insights Page - Comprehensive KPI tracking and analytics
 * Based on Videxa's Revenue Cycle Insights solution
 */
const RevenueCycleInsightsPage = () => {
  return (
    <div className="revenue-cycle-insights-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Revenue Cycle <span className="text-blue-300">Insights</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              Provides your organization industry-leading visibility into your KPIs with real-time actionable insights.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors">
                Get Started
              </button>
              <button className="border-2 border-blue-300 text-blue-300 hover:bg-blue-300 hover:text-blue-900 px-8 py-4 rounded-lg font-semibold transition-colors">
                Browse Features
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Key Statistics */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">65%</div>
              <p className="text-gray-600 dark:text-gray-300">Reduction in Denial Rates</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">22%</div>
              <p className="text-gray-600 dark:text-gray-300">Revenue Cycle Improvement</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">94%</div>
              <p className="text-gray-600 dark:text-gray-300">First-Pass Claims Acceptance</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600 mb-2">24/7</div>
              <p className="text-gray-600 dark:text-gray-300">Support & Monitoring</p>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-gray-900 dark:text-white text-center">
              From Visualization to Insight and Action
            </h2>
            <div className="text-lg text-gray-700 dark:text-gray-300 space-y-6">
              <p>
                Coming out of the pandemic, we learned that it has never been more critical to leverage your data in a transformational way. Reporting is not one size fits all. Sometimes you need to see your business at a high level, sometimes more granular. And sometimes, you need to get a holistic view of your business from all systems you use in your business.
              </p>
              <p>
                But always, you want to make sure you are getting the <strong>information you need in order to make the right decisions upon which you are able to action.</strong>
              </p>
              <p>
                <strong>We understand "overload of information".</strong> Our point solutions are developed with outcomes in mind and help shorten time to insights to support more intentional decision making and action. Our clients are Hospitals, Health Systems, and Physician Practices.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Revenue Cycle KPIs Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-12 text-gray-900 dark:text-white text-center">
            Revenue Cycle Insights KPIs
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12 max-w-4xl mx-auto">
            The Revenue Cycle Insights Solution leverages MAP Keys which are publicly available industry-standard metrics or KPIs used to track your organization's revenue cycle performance using objective, consistent calculations.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Financial Management */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold mb-4 text-blue-600">Financial Management</h3>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Net days in A/R</li>
                <li>• Cash collections as % of net patient service revenue</li>
                <li>• Cost to collect</li>
                <li>• Cost to collect by functional area</li>
              </ul>
            </div>

            {/* Claims */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold mb-4 text-green-600">Claims</h3>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Clean claim rate</li>
                <li>• Late charges as % of total charges</li>
              </ul>
            </div>

            {/* Pre-Billing */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold mb-4 text-purple-600">Pre-Billing</h3>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Days in total discharged not final billed (DNFB)</li>
                <li>• Days in final billed not submitted to payer (FBNS)</li>
                <li>• Days in total discharged not submitted to payer (DNSP)</li>
                <li>• Total charge lag days</li>
              </ul>
            </div>

            {/* Account Resolution */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold mb-4 text-orange-600">Account Resolution</h3>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Aged A/R as % of total billed A/R</li>
                <li>• Aged A/R by payer group</li>
                <li>• Remittance denial rate</li>
                <li>• Denial write-offs as % of net patient service revenue</li>
                <li>• Bad debt</li>
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
            {/* Patient Access */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold mb-4 text-red-600">Patient Access</h3>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li>• Service authorization rate - Inpatient and/or observation</li>
                <li>• Service authorization rate for outpatient encounters</li>
                <li>• Conversion rate of uninsured patients to third-party funding source</li>
                <li>• Point-of-service cash collections</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* AI and Real-time Data Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-8 text-gray-900 dark:text-white text-center">
            Artificial Intelligence (AI) and Real-time Data
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12 max-w-4xl mx-auto">
            Creating a new paradigm for healthcare through predictive and prescriptive analytics
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-blue-100 dark:bg-blue-900 p-6 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl font-bold text-blue-600 dark:text-blue-300">1</span>
              </div>
              <h3 className="text-lg font-bold mb-3 text-gray-900 dark:text-white">Predictive Approach</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Real-time insights and data that create a forecasting system to alert healthcare leaders to shifting fronts that may impact business
              </p>
            </div>

            <div className="text-center">
              <div className="bg-green-100 dark:bg-green-900 p-6 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl font-bold text-green-600 dark:text-green-300">2</span>
              </div>
              <h3 className="text-lg font-bold mb-3 text-gray-900 dark:text-white">Determining Best Practices</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Comparing data from multiple facilities to help increase efficiencies and effectiveness throughout business
              </p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 dark:bg-purple-900 p-6 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl font-bold text-purple-600 dark:text-purple-300">3</span>
              </div>
              <h3 className="text-lg font-bold mb-3 text-gray-900 dark:text-white">Proven Recommendations</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Recommended actions based on financial insights derived through ML algorithms enable immediate action
              </p>
            </div>

            <div className="text-center">
              <div className="bg-orange-100 dark:bg-orange-900 p-6 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl font-bold text-orange-600 dark:text-orange-300">4</span>
              </div>
              <h3 className="text-lg font-bold mb-3 text-gray-900 dark:text-white">Detection & Prevention</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Early detection of payor mix ratios, physician behavior shifts, and volume changes by service lines
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Operational Analytics Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-8 text-gray-900 dark:text-white text-center">
            Gain Actionable Insights with Operational Analytics
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12 max-w-4xl mx-auto">
            Operational analytics focuses on the use of data and analytics to improve the efficiency or effectiveness of systems used to provide and manage care processes.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg text-center">
              <h3 className="text-xl font-bold mb-4 text-blue-600">Readmissions Management</h3>
              <p className="text-gray-700 dark:text-gray-300">
                Predict and prevent readmissions through advanced analytics and patient risk assessment
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg text-center">
              <h3 className="text-xl font-bold mb-4 text-green-600">Claims Management</h3>
              <p className="text-gray-700 dark:text-gray-300">
                Optimize claims processing with AI-driven validation and automated denial prevention
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg text-center">
              <h3 className="text-xl font-bold mb-4 text-purple-600">Staffing Management</h3>
              <p className="text-gray-700 dark:text-gray-300">
                Optimize workforce allocation based on predictive analytics and patient volume forecasting
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg text-center">
              <h3 className="text-xl font-bold mb-4 text-orange-600">Throughput Management</h3>
              <p className="text-gray-700 dark:text-gray-300">
                Improve patient flow and operational efficiency through real-time monitoring and analytics
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">
            AgentNexus Can Help
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            AgentNexus brings disparate healthcare data sets together into one consolidated environment for comprehensive revenue cycle insights.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-900 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors">
              Request Demo
            </button>
            <button className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-4 rounded-lg font-semibold transition-colors">
              View Our Plans
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RevenueCycleInsightsPage; 